import type ListenerSetter from '@helpers/listenerSetter';
import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import simulateEvent from '@helpers/dom/dispatchEvent';
import {getAppWindow, onAppWindowChange} from '@helpers/appWindow';
import buttonKeyDown from '@helpers/solid/buttonKeyDown';
import Modes from '@config/modes';

let lastMouseDownElement: HTMLElement;
const onGlobalMouseDown = (e: MouseEvent) => {
  lastMouseDownElement = e.target as HTMLElement;
  // if((lastMouseDownElement as any)?.cancelMouseDown) {
  if(lastMouseDownElement?.closest('[cancel-mouse-down]')) {
    e.preventDefault();
  }
};

// The desktop `attachClickEvent` path suppresses any click whose target !== the last mousedown target
// (`hasMouseMovedSinceDown`). That tracker MUST listen on the document the app currently lives in —
// otherwise, while the client is popped out into a Document PiP window, mousedowns there never update
// `lastMouseDownElement` and EVERY real (isTrusted) click in the pip is swallowed. Re-bind to the
// active app window's document, following the client into / out of the pip.
let mouseDownDocument: Document;
const bindMouseDownTracker = (win: Window) => {
  mouseDownDocument?.removeEventListener('mousedown', onGlobalMouseDown);
  mouseDownDocument = win.document;
  mouseDownDocument.addEventListener('mousedown', onGlobalMouseDown);
};
bindMouseDownTracker(getAppWindow());
onAppWindowChange((win) => bindMouseDownTracker(win));

export function hasMouseMovedSinceDown(e: Event) {
  // Native controls activated from the keyboard dispatch a trusted click
  // without a preceding mousedown. `detail === 0` distinguishes that path
  // (and assistive-technology activation) from a pointer click.
  if(Modes.a11y && e.type === 'click' && (e as MouseEvent).detail === 0) {
    return false;
  }

  if(e.isTrusted && e.type === 'click' && e.target !== lastMouseDownElement) {
    return true;
  }
}

export const CLICK_EVENT_NAME: 'mousedown' /* | 'touchend' */ | 'click' = (IS_TOUCH_SUPPORTED ? 'mousedown' : 'click') as any;
export type AttachClickOptions = AddEventListenerOptions & Partial<{listenerSetter: ListenerSetter, cancelMouseDown?: boolean, touchMouseDown: boolean, ignoreMove: boolean}>;
export function attachClickEvent(elem: HTMLElement | Window, callback: (e: /* TouchEvent |  */MouseEvent) => void, options: AttachClickOptions = {}) {
  const add = options.listenerSetter ? options.listenerSetter.add(elem) : elem.addEventListener.bind(elem);
  const remove = options.listenerSetter ? options.listenerSetter.removeManual.bind(options.listenerSetter, elem) : elem.removeEventListener.bind(elem);
  const listenerOptions = options.once ? {...options, once: false} : options;

  options.touchMouseDown = true;
  /* if(options.touchMouseDown && CLICK_EVENT_NAME === 'touchend') {
    add('mousedown', callback, options);
  } else if(CLICK_EVENT_NAME === 'touchend') {
    const o = {...options, once: true};

    const onTouchStart = (e: TouchEvent) => {
      const onTouchMove = (e: TouchEvent) => {
        remove('touchmove', onTouchMove, o);
        remove('touchend', onTouchEnd, o);
      };

      const onTouchEnd = (e: TouchEvent) => {
        remove('touchmove', onTouchMove, o);
        callback(e);
        if(options.once) {
          remove('touchstart', onTouchStart);
        }
      };

      add('touchend', onTouchEnd, o);
      add('touchmove', onTouchMove, o);
    };

    add('touchstart', onTouchStart);
  } else {
    add(CLICK_EVENT_NAME, callback, options);
  } */

  if(options.cancelMouseDown) {
    (elem as HTMLElement).setAttribute('cancel-mouse-down', '');
    // (elem as any).cancelMouseDown = true;
  }

  const element = elem as HTMLElement;
  const isHtmlElement = typeof(element.getAttribute) === 'function' && typeof(element.click) === 'function';
  const isNativeInteractive = isHtmlElement &&
    element.matches('button, input, select, textarea, a[href]');
  let detached = false;

  const detach = () => {
    if(detached) return;
    detached = true;
    remove(CLICK_EVENT_NAME, onPrimaryActivate, listenerOptions);
    if(onKeyboardClick) remove('click', onKeyboardClick, listenerOptions);
    if(onKeyDown) remove('keydown', onKeyDown, listenerOptions);
  };

  const invokeCallback = (e: MouseEvent) => {
    try {
      callback(e);
    } finally {
      if(options.once) detach();
    }
  };

  const onPrimaryActivate = (e: MouseEvent) => {
    if(CLICK_EVENT_NAME === 'click' && !options.ignoreMove && hasMouseMovedSinceDown(e)) {
      if(options.once) detach();
      return;
    }

    invokeCallback(e);
  };

  // Touch-capable devices listen to mousedown for pointer activation, but a
  // hardware keyboard and assistive technologies activate native controls with
  // a synthetic click (detail === 0). Handle that click without duplicating the
  // pointer click that follows an already-handled mousedown.
  const onKeyboardClick = Modes.a11y && CLICK_EVENT_NAME !== 'click' && isHtmlElement ? (e: MouseEvent) => {
    if(e.detail === 0) invokeCallback(e);
  } : undefined;

  // Non-native role=button elements do not receive the browser's implicit
  // keyboard click. Generate one so both Solid onClick and this helper use the
  // same semantic activation path on desktop, touch and hybrid devices.
  const onKeyDown = Modes.a11y && isHtmlElement && !isNativeInteractive ? (e: KeyboardEvent) => {
    if(element.getAttribute('role') === 'button') buttonKeyDown(e);
  } : undefined;

  add(CLICK_EVENT_NAME, onPrimaryActivate, listenerOptions);
  if(onKeyboardClick) add('click', onKeyboardClick, listenerOptions);
  if(onKeyDown) add('keydown', onKeyDown, listenerOptions);

  return detach;
}

// export function detachClickEvent(elem: HTMLElement | Window, callback: (e: /* TouchEvent |  */MouseEvent) => void, options?: AddEventListenerOptions) {
//   // if(CLICK_EVENT_NAME === 'touchend') {
//   //   elem.removeEventListener('touchstart', callback, options);
//   // } else {
//   elem.removeEventListener(CLICK_EVENT_NAME, callback as any, options);
//   // }
// }

export function simulateClickEvent(elem: HTMLElement) {
  simulateEvent(elem, CLICK_EVENT_NAME);
}
